import {
  type DeviceInfo,
  type DeviceInfoVisibilityConfig,
  deviceInfoPlatformFlagKeys,
} from "shared/types/DeviceInfo";

type MergeDeviceInfoVisibilityConfigProps = {
  /** The narrower config, e.g. a route's `platformConfig.backButton`. */
  config?: DeviceInfoVisibilityConfig;
  /** The config `config` falls back to, e.g. the society default. */
  defaultConfig?: DeviceInfoVisibilityConfig;
};

/**
 * Merges two visibility configs key by key, so a route that only decides
 * `isIOS` still takes `isNativeApp`, orientation and the screen bounds from the
 * society default. A key that is absent or `undefined` in `config` is "not
 * decided" and falls back; an explicit `false` does not.
 */
export const mergeDeviceInfoVisibilityConfig = ({
  config,
  defaultConfig,
}: MergeDeviceInfoVisibilityConfigProps): DeviceInfoVisibilityConfig => {
  const decided = Object.fromEntries(
    Object.entries(config ?? {}).filter(([, value]) => value !== undefined),
  );
  return { ...defaultConfig, ...decided };
};

type IsWithinScreenConstraintsProps = {
  /** The device being checked. */
  device: DeviceInfo;
  /** The merged config whose orientation and bounds apply. */
  config: DeviceInfoVisibilityConfig;
};

/**
 * Whether the device's screen meets every screen constraint in `config`. A
 * constraint the device cannot be measured against - no orientation or size
 * reported - is treated as met, so a missing measurement never hides a button.
 */
export const isWithinScreenConstraints = ({
  device,
  config,
}: IsWithinScreenConstraintsProps): boolean => {
  if (
    config.orientation !== undefined &&
    device.orientation !== undefined &&
    config.orientation !== device.orientation
  ) {
    return false;
  }
  if (
    config.maxWidth !== undefined &&
    device.width !== undefined &&
    device.width > config.maxWidth
  ) {
    return false;
  }
  if (
    config.maxHeight !== undefined &&
    device.height !== undefined &&
    device.height > config.maxHeight
  ) {
    return false;
  }
  return true;
};

type ResolveDeviceInfoVisibilityProps = MergeDeviceInfoVisibilityConfigProps & {
  /** The device the element would show on. */
  device: DeviceInfo;
};

/**
 * Decides whether an element configured by `DeviceInfoVisibilityConfig` shows
 * on `device`.
 *
 * - Only the flags that describe the device count: on the iOS app that is
 *   `isNativeApp` and `isIOS`, in a browser it is `isWeb`.
 * - Any of those decided `false` hides the element; otherwise any decided
 *   `true` shows it.
 * - Orientation and the screen bounds only narrow an element that shows; they
 *   never turn one on.
 *
 * Returns `undefined` when no flag that describes the device is decided, so the
 * caller keeps its own behaviour rather than reading silence as "hide".
 */
export const resolveDeviceInfoVisibility = ({
  device,
  config,
  defaultConfig,
}: ResolveDeviceInfoVisibilityProps): boolean | undefined => {
  const merged = mergeDeviceInfoVisibilityConfig({ config, defaultConfig });

  const decided = deviceInfoPlatformFlagKeys
    .filter((flagKey) => device[flagKey])
    .map((flagKey) => merged[flagKey])
    .filter((value): value is boolean => value !== undefined);

  if (decided.length === 0) {
    return undefined;
  }
  if (decided.includes(false)) {
    return false;
  }
  return isWithinScreenConstraints({ device, config: merged });
};
